import dayjs from 'utils/dayjs'

import { Color } from 'themes'
import {
  FileDelivery,
  FileDeliveryPattern,
  FileDeliveryStatus,
  JobStatus,
  JobSummary,
  RunningJob,
  RunningPhase
} from 'redux/selectors/internal/DataRuns'

/** How stale `latest_ingested_at` may be before the page flags it. */
export const INGEST_STALENESS_HOURS = 26

/**
 * Identity of a job as run for one customer - the unit the page reports on,
 * and what row keys and job lookups have to use. The job name alone is not
 * unique: the global singleton jobs (`calculate_targets`, `validate_kpis`,
 * …) share one name across every customer they run for, so a name-keyed map
 * keeps one arbitrary customer's run and a name-keyed row list produces
 * duplicate React keys. Mirrors the backend's own (job_name, customer_id)
 * grouping; the separator is a null byte because a Glue job name can
 * contain spaces and hyphens but never that.
 */
export function jobLookupKey(
  jobName: string,
  customerId?: string | null
): string {
  return `${jobName}\u0000${customerId ?? ''}`
}

export function jobKey(job: JobSummary): string {
  return jobLookupKey(job.job_name, job.customer?.id)
}

/**
 * How far back from the most recent run another run can start and still
 * belong to the same batch. Matches the timeline's own lookback so the
 * period in the page header and the timeline's window agree.
 */
const RUN_BATCH_LOOKBACK_HOURS = 18

export const STATUS_LABEL: Record<JobStatus, string> = {
  ok: 'Lyckad',
  fail: 'Misslyckad',
  timeout: 'Timeout',
  running: 'Pågår'
}

/** Tag/Chip `color` prop per job status. */
export const STATUS_COLOR: Record<
  JobStatus,
  Color | 'error' | 'success' | 'info'
> = {
  ok: 'success',
  fail: 'error',
  timeout: 'tertiary',
  running: 'info'
}

/** Consecutive-failure count from which "I rad" gets highlighted. */
export const STREAK_HIGHLIGHT_THRESHOLD = 3

/**
 * Fill color per status for the timeline bars and the 14-day status grid -
 * palette references only, no new tones or hex literals (see CLAUDE.md).
 */
export const STATUS_FILL_COLOR: Record<JobStatus, string> = {
  ok: 'success.main',
  fail: 'error.main',
  timeout: 'tertiary.backgroundDark',
  running: 'info.light'
}

/** Border color per status, one shade deeper than `STATUS_FILL_COLOR`. */
export const STATUS_BORDER_COLOR: Record<JobStatus, string> = {
  ok: 'success.dark',
  fail: 'error.dark',
  timeout: 'tertiary.main',
  running: 'info.main'
}

/** Fill/border for a day with no run at all - distinct from any status. */
export const NO_RUN_CELL_COLOR = { bg: 'grey.200', border: 'grey.300' }

/** Opacity pulse for a running job's bar/cell - the page's one animation. */
export const RUNNING_PULSE_SX = {
  '@keyframes dataRunsPulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.45 }
  },
  animation: 'dataRunsPulse 1.8s ease-in-out infinite'
}

export function formatClock(iso: string | null): string {
  if (!iso) {
    return '–'
  }

  return dayjs(iso).tz().format('HH:mm')
}

/**
 * Clock time only for a timestamp from today, date and clock otherwise.
 * Used where the value can be days old - a job failing every night has a
 * last success far back, and a bare "00:18" there reads as last night.
 */
export function formatDayClock(iso: string | null): string {
  if (!iso) {
    return '–'
  }

  const value = dayjs(iso).tz()

  if (value.isSame(dayjs().tz(), 'day')) {
    return value.format('HH:mm')
  }

  return value.format('D MMM HH:mm')
}

export function formatDurationMin(durationMin: number | null): string {
  if (durationMin === null) {
    return '–'
  }

  return `${Math.round(durationMin)} min`
}

/**
 * Duration in the largest units that still say something: seconds below a
 * minute (a job 20 seconds in should not read "0 min"), whole minutes up to
 * an hour, hours and minutes above that. `formatDurationMin` stays as it is
 * for the table columns, which only ever show completed nightly runs.
 */
export function formatDurationLong(minutes: number): string {
  const safeMinutes = Math.max(0, minutes)

  if (safeMinutes < 1) {
    return `${Math.round(safeMinutes * 60)} s`
  }

  const rounded = Math.round(safeMinutes)

  if (rounded < 60) {
    return `${rounded} min`
  }

  const hours = Math.floor(rounded / 60)
  const rest = rounded % 60

  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`
}

export const RUNNING_PHASE_LABEL: Record<RunningPhase, string> = {
  starting: 'Startar',
  running: 'Pågår',
  stopping: 'Avbryts'
}

/**
 * How far past its expected finish a job may run before it is called out.
 * Some slack, because a job that normally takes 7 minutes taking 7.5 is
 * noise, not news.
 */
const OVERDUE_FACTOR = 1.25

export interface RunningProgress {
  elapsedMin: number
  /** Null whenever the run has no estimate to count down against. */
  remainingMin: number | null
  /** Null when there is no estimate; capped below 100 while still running. */
  progressPercent: number | null
  isOverdue: boolean
  overdueMin: number
}

/**
 * Everything the row shows about where a run is, derived from the anchors
 * the API sends plus the current time. Kept out of the component so the
 * clock is the only thing that has to tick.
 */
export function describeRunningProgress(
  job: RunningJob,
  nowMs: number
): RunningProgress {
  const elapsedMin = Math.max(
    0,
    (nowMs - new Date(job.started_at).getTime()) / 60000
  )

  if (job.expected_duration_min === null || job.expected_duration_min <= 0) {
    return {
      elapsedMin,
      remainingMin: null,
      progressPercent: null,
      isOverdue: false,
      overdueMin: 0
    }
  }

  const remainingMin = job.expected_duration_min - elapsedMin

  return {
    elapsedMin,
    remainingMin: Math.max(0, remainingMin),
    // Never a full bar: the run is by definition not done, and a bar
    // sitting at 100% reads as finished-but-not-updated.
    progressPercent: Math.min(
      99,
      (elapsedMin / job.expected_duration_min) * 100
    ),
    isOverdue: elapsedMin > job.expected_duration_min * OVERDUE_FACTOR,
    overdueMin: Math.max(0, -remainingMin)
  }
}

/**
 * When the last of the running jobs is expected to land - the one number
 * that answers "how long until this is over". Null while nothing running
 * has an estimate.
 */
export function latestExpectedFinish(jobs: RunningJob[]): string | null {
  const finishes = jobs
    .map((job) => job.expected_finish_at)
    .filter((value): value is string => value !== null)

  if (finishes.length === 0) {
    return null
  }

  return finishes.reduce((max, value) => (value > max ? value : max))
}

/**
 * Two decimals under $100 (single-job/small runs are often cents-to-a-few-
 * dollars, where whole dollars would round away all the signal), whole
 * dollars at or above $100.
 */
export function formatCostUsd(costUsd: number | null): string {
  if (costUsd === null) {
    return '–'
  }

  const decimals = Math.abs(costUsd) < 100 ? 2 : 0

  const formatted = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals
  }).format(costUsd)

  return `$${formatted}`
}

/**
 * Share of the jobs in scope that failed, as whole percent. A non-zero
 * share that rounds to 0 becomes "<1 %" so a real failure is never
 * reported as none. Non-breaking space before the sign, and null when
 * there is nothing to divide by.
 */
export function formatFailRate(
  failCount: number,
  jobCount: number
): string | null {
  if (jobCount <= 0) {
    return null
  }

  const percent = (failCount / jobCount) * 100

  if (percent > 0 && percent < 1) {
    return '<1\u00a0%'
  }

  return `${Math.round(percent)}\u00a0%`
}

export function formatDpuHours(dpuHours: number | null): string {
  if (dpuHours === null) {
    return '–'
  }

  return `${dpuHours.toFixed(2)} DPU-h`
}

export function isIngestStale(latestIngestedAt: string | null): boolean {
  if (!latestIngestedAt) {
    return false
  }

  const ageMs = Date.now() - new Date(latestIngestedAt).getTime()

  return ageMs > INGEST_STALENESS_HOURS * 60 * 60 * 1000
}

/**
 * The period the page's figures actually cover, spelled out so "senaste
 * körningen" and "Jobb i natt" are not left to the reader's guess.
 *
 * Scoped to the most recent batch (see RUN_BATCH_LOOKBACK_HOURS): a job
 * that last ran days ago would otherwise stretch the stated period back
 * that far, which reads as if the whole run took days.
 */
export function formatRunPeriod(jobs: JobSummary[]): string | null {
  if (jobs.length === 0) {
    return null
  }

  const starts = jobs.map((job) => dayjs(job.started_at).tz())
  const latestStart = starts.reduce(
    (max, start) => (start.isAfter(max) ? start : max),
    starts[0]
  )
  const cutoff = latestStart.subtract(RUN_BATCH_LOOKBACK_HOURS, 'hour')

  const batch = jobs.filter(
    (job) => !dayjs(job.started_at).tz().isBefore(cutoff)
  )

  if (batch.length === 0) {
    return null
  }

  const start = batch
    .map((job) => dayjs(job.started_at).tz())
    .reduce((min, value) => (value.isBefore(min) ? value : min))

  // A job still running has no end; the period then runs up to now.
  const end = batch
    .map((job) =>
      job.completed_at ? dayjs(job.completed_at).tz() : dayjs().tz()
    )
    .reduce((max, value) => (value.isAfter(max) ? value : max))

  if (start.isSame(end, 'day')) {
    return `${start.format('D MMMM HH:mm')}–${end.format('HH:mm')}`
  }

  return `${start.format('D MMMM HH:mm')} – ${end.format('D MMMM HH:mm')}`
}

/* ------------------------------------------------------------------ *
 * File deliveries
 *
 * The files customers deliver over SFTP, by email or by uploading them in
 * the portal. Their own status vocabulary, kept apart from JobStatus: a
 * Glue job can never be "missing", and widening JobStatus would force the
 * four exhaustive Record<JobStatus, …> maps above to invent colors for
 * states their subject cannot reach.
 * ------------------------------------------------------------------ */

export const DELIVERY_STATUS_LABEL: Record<FileDeliveryStatus, string> = {
  ok: 'Levererad',
  fail: 'Fel i filen',
  late: 'Försenad',
  missing: 'Uteblev',
  stopped: 'Slutat leverera',
  unknown: 'Okänt intervall'
}

/**
 * What we know about a source that has no cadence to be judged against.
 * Shown in place of 'unknown' so the row says *why* nothing is expected of
 * it - "Engångsfil" and "Ingen historik" are different situations, and
 * "Okänt intervall" named neither.
 */
export const DELIVERY_PATTERN_LABEL: Record<FileDeliveryPattern, string> = {
  scheduled: 'Återkommande',
  irregular: 'Oregelbunden',
  one_time: 'Engångsfil',
  none: 'Ingen historik'
}

/**
 * The label on a delivery's status tag.
 *
 * 'unknown' on its own is uninformative, and it is by far the most common
 * status now that a source has to prove it delivers on a schedule before it
 * can be late: the pattern is what the reader needs. Every other status
 * describes a delivery that actually happened (or failed to), and speaks for
 * itself.
 */
export function deliveryStatusLabel(delivery: FileDelivery): string {
  return delivery.status === 'unknown'
    ? DELIVERY_PATTERN_LABEL[delivery.delivery_pattern]
    : DELIVERY_STATUS_LABEL[delivery.status]
}

/**
 * Tag/Chip `color` prop per delivery status. `warning` for 'late' because a
 * delayed file is not yet a failure; `default` for 'unknown' because it
 * describes a gap in our own history rather than a state of the source. No
 * new tones - both already exist in the theme palette.
 *
 * 'stopped' is `warning` rather than `error` even though it is a problem:
 * the sort already puts it last, and a feed that died in the spring
 * shouting as loudly as tonight's failure is what makes a list stop being
 * read.
 */
export const DELIVERY_STATUS_COLOR: Record<
  FileDeliveryStatus,
  Color | 'error' | 'success' | 'warning' | 'default'
> = {
  ok: 'success',
  fail: 'error',
  late: 'warning',
  missing: 'error',
  stopped: 'warning',
  unknown: 'default'
}

/**
 * Fill per delivery status for a lane dot or row marker describing the
 * *source* over time. `DELIVERY_FILL_COLOR` below is the one for a single
 * recorded arrival - palette references only, no new tones.
 */
export const DELIVERY_STATUS_FILL_COLOR: Record<FileDeliveryStatus, string> = {
  ok: 'success.main',
  fail: 'error.main',
  late: 'warning.main',
  missing: 'error.light',
  stopped: 'warning.light',
  unknown: 'grey.400'
}

/**
 * Fill/border for a delivery cell or marker. Only the two statuses a
 * recorded delivery can actually have: 'late', 'missing' and 'unknown'
 * describe a source over time, not a single arrival, so they never get a
 * mark of their own.
 */
export const DELIVERY_FILL_COLOR: Record<'ok' | 'fail', string> = {
  ok: 'success.main',
  fail: 'error.main'
}

export const DELIVERY_BORDER_COLOR: Record<'ok' | 'fail', string> = {
  ok: 'success.dark',
  fail: 'error.dark'
}

export function isDeliveryProblem(status: FileDeliveryStatus): boolean {
  return (
    status === 'fail'
    || status === 'late'
    || status === 'missing'
    || status === 'stopped'
  )
}

export function deliveryKey(delivery: FileDelivery): string {
  return delivery.active_source_id
}

/**
 * A source's own label. `display_name` is free text the customer's setup
 * decides and is empty on some live sources, so the connector name is the
 * fallback rather than letting a row render nameless.
 */
export function deliveryLabel(delivery: FileDelivery): string {
  return delivery.display_name.trim() || delivery.source_name || 'Namnlös källa'
}

/**
 * Cadence in the coarsest unit that still says something. Never a raw hour
 * count - "var 168:e timme" is unreadable where "varje vecka" is not.
 */
export function formatIntervalHours(hours: number | null): string {
  if (hours === null) {
    return '–'
  }

  if (hours < 2) {
    return 'varje timme'
  }

  if (hours < 36) {
    return 'varje dygn'
  }

  const days = Math.round(hours / 24)

  if (hours < 200) {
    return `var ${days}:e dag`
  }

  const weeks = Math.round(days / 7)

  return weeks <= 1 ? 'varje vecka' : `var ${weeks}:e vecka`
}

/**
 * How overdue a delivery is, in the unit that reads best. Empty string
 * rather than a dash when nothing is overdue, so the caller can leave the
 * line out entirely instead of rendering a placeholder.
 */
export function formatOverdueHours(hours: number | null): string {
  if (hours === null || hours <= 0) {
    return ''
  }

  if (hours < 24) {
    return `${Math.round(hours)} h sen`
  }

  const days = Math.round(hours / 24)

  return days === 1 ? '1 dygn sen' : `${days} dygn sen`
}

/**
 * "9 av 10 filer" for a source that drops a set of files per delivery.
 * Empty string when the source sends a single file, or when we have no
 * expectation to compare against.
 */
export function formatFileCount(
  fileCount: number | null,
  expectedFileCount: number | null
): string {
  if (fileCount === null || expectedFileCount === null) {
    return ''
  }

  return `${fileCount} av ${expectedFileCount} filer`
}

/** Whether the latest drop was short of the files the source usually sends. */
export function isDeliveryIncomplete(delivery: FileDelivery): boolean {
  return (
    delivery.file_count !== null &&
    delivery.expected_file_count !== null &&
    delivery.file_count < delivery.expected_file_count
  )
}
